import React from "react";
import styles from "./ReportFilterBase.module.scss";
import {
    Button,
    Text,
    Flex,
    Pill,
    Checkbox,
    Popup,
    RadioGroup,
    CloseIcon,
    Segment,
    ChevronEndIcon,
} from "@fluentui/react-northstar";
import { Selectable } from "../../models/abstractions/Selectable";

export interface ReportFilterBaseProps {
    icon: React.ReactNode;
    prefix: string;
    items: Selectable[];
    multiple?: boolean;
    onSelectedItemsChanged: (items: Selectable[]) => void;
    clearable?: boolean;
    atLeastOneAlwaysSelected?: boolean;
    isMobileView?: boolean;
}

export const ReportFilterBase: React.FC<ReportFilterBaseProps> = ({
    icon,
    prefix,
    items,
    multiple,
    onSelectedItemsChanged,
    clearable,
    atLeastOneAlwaysSelected,
    isMobileView,
}) => {
    const selectedItems = items.filter((item) => item.selected);

    const triggerContent = (
        <Flex vAlign="center">
            <div style={{ paddingRight: "3px" }}>{prefix}</div>
            {selectedItems.map((selectedItem) => (
                <Pill
                    key={selectedItem.key}
                    content={selectedItem.content}
                    size="smaller"
                    appearance="outline"
                    className={styles.pill}
                />
            ))}
            {clearable && selectedItems && selectedItems.length > 0 && (
                <CloseIcon
                    style={{ color: "red", paddingLeft: "3px" }}
                    onClick={() => {
                        const newItems = items.slice();
                        newItems.forEach((item) => (item.selected = false));
                        onSelectedItemsChanged(newItems);
                    }}
                />
            )}
        </Flex>
    );

    const triggerContentMobile = (
        <Flex space="between" vAlign="center">
            <Flex vAlign="center" column styles={{ paddingBottom: "2px" }}>
                <Text
                    styles={({ theme: { siteVariables } }) => ({
                        color: siteVariables.colorScheme.default.foreground5,
                    })}
                    size={items.findIndex((i) => i.selected) > -1 ? "small" : undefined}
                >
                    {prefix}
                </Text>
                {selectedItems && selectedItems.length > 0 && (
                    <Flex vAlign="center">
                        {clearable && (
                            <CloseIcon
                                styles={({ theme: { siteVariables } }) => ({
                                    color: siteVariables.colorScheme.default.foreground5,
                                    paddingRight: "6px",
                                })}
                                onClick={() => {
                                    const newItems = items.slice();
                                    newItems.forEach((item) => (item.selected = false));
                                    onSelectedItemsChanged(newItems);
                                }}
                            />
                        )}
                        {selectedItems.map((selectedItem, idx) => (
                            <Text key={selectedItem.key}>{`${idx > 0 ? ", " : ""}${selectedItem.content}`}</Text>
                        ))}
                    </Flex>
                )}
            </Flex>
            <ChevronEndIcon
                size="large"
                styles={({ theme: { siteVariables } }) => ({
                    color: siteVariables.colorScheme.default.foreground5,
                })}
            />
        </Flex>
    );

    const trigger = <Button
        style={{margin: "4px", marginRight: "0"}}
        icon={icon} content={triggerContent}
        fluid
        className={styles.button} />;
    const triggerMobile = (
        <Segment
            styles={({ theme: { siteVariables } }) => ({
                color: siteVariables.colorScheme.default.foreground5,
                paddingTop: "4px",
                paddingBottom: "4px",
                width: "100%",
                backgroundColor: siteVariables.colorScheme.default.background1,
            })}
            content={triggerContentMobile}
        />
    );

    let content: React.ReactNode;
    if (multiple) {
        content = (
            <Flex column>
                {items.map((item) => (
                    <Checkbox
                        key={item.key}
                        label={item.content}
                        checked={item.selected}
                        disabled={atLeastOneAlwaysSelected && selectedItems.length == 1 && item.selected}
                        onChange={() => {
                            const newItems = items.slice();
                            const checkedItem = newItems.find((newItem) => newItem.key === item.key);
                            if (checkedItem) {
                                checkedItem.selected = !checkedItem.selected;
                            }
                            onSelectedItemsChanged(newItems);
                        }}
                    />
                ))}
            </Flex>
        );
    } else {
        const selectedItemValue = selectedItems.length > 0 ? selectedItems[0].key : undefined;
        content = (
            <RadioGroup
                vertical
                checkedValue={selectedItemValue}
                items={items.map((item): any => ({
                    key: item.key,
                    label: item.content,
                    value: item.key,
                    className: styles.radioItem,
                }))}
                onCheckedValueChange={(_, data) => {
                    const newItems = items.slice();
                    newItems.forEach((item) => (item.selected = false));
                    const checkedItem = newItems.find((item) => item.key === data?.value);
                    if (checkedItem) {
                        checkedItem.selected = true;
                    }
                    onSelectedItemsChanged(newItems);
                }}
            />
        );
    }

    return <Popup trigger={isMobileView ? triggerMobile : trigger} content={content} position="below" align="end" />;
};
